import { interactiveSitePlanSvg } from 'constants/svg-assets';
import { priceReducedSvgHtml } from '../constants/svgIcons';

export default class SitePlanTooltip {
  /**
   * setInformation function is in charge of fill the information needed to build the site plan tooltip.
   * @param {Object} information related to the community where the site plan belongs.
   * @param {Boolean} hasInteractiveSitePlan indicates if the site plan is interactive or not.
   */
  setInformation(information, hasInteractiveSitePlan) {
    this.hasInteractiveSitePlan = hasInteractiveSitePlan;
    this.sitePlanCenterLatitude = information.SitePlanCenterLatitude;
    this.sitePlanCenterLongitude = information.SitePlanCenterLongitude;
    this.communityId = information.Id;
    this.priceText = SitePlanTooltip.getPriceText(information.Price);
    this.title = information.Name;
    this.detailText = SitePlanTooltip.getDetailText(information.FormattedBedrooms, information.FormattedBathrooms);
    this.priceReducedText = SitePlanTooltip.getPriceReducedText(information.PriceReduced);
  }

  /**
   * getDetailText: retrieves information about bathrooms and bedrooms facts.
   * @param {string} formattedBedrooms of the community.
   * @param {string} formattedBathrooms of the community.
   * @returns a string indicating bathrooms and bedrooms of the community where the site plan belongs.
   */
  static getDetailText(formattedBedrooms, formattedBathrooms) {
    const detailText =
      (formattedBedrooms ? `${formattedBedrooms} Bed` : '') +
      (formattedBedrooms && formattedBathrooms ? ' / ' : '') +
      (formattedBathrooms ? `${formattedBathrooms} Ba` : '');

    return detailText;
  }

  /**
   * getPriceReducedText: returns a string indicating the price reduction text for the community.
   * @param {decimal} priceReduced is the price reduction.
   * @returns a string representing the price reduction community text.
   */
  static getPriceReducedText(priceReduced) {
    return priceReduced
        ? `Up to ${priceReduced}` 
        : null;
  }

  /**
   * getPriceText functino will return the community price.
   * @param {decimal} price of the community.
   * @returns a string representing the community price.
   */
  static getPriceText(price) {
    return price ? `From ${price}+` : '';
  }

  /**
   * getInteractiveSitePlanSection: adds a button with the information needed to reach
   * the interactive site plan.
   */
  getInteractiveSitePlanSection() {
    if (!this.hasInteractiveSitePlan) {
      return '';
    }

    return `<button
                class="nhs-c-button--text-only nhs-c-button--primary-ui nhs-c-button--small-32 nhs-u-flex-row nhs-u-gap--200"
                data-interactive-plan-button
                data-latitude="${this.sitePlanCenterLatitude}"
                data-longitude="${this.sitePlanCenterLongitude}"
                data-community-id="${this.communityId}">
                <span class='nhs-c-icon--16'>${interactiveSitePlanSvg}</span> Interactive Map
            </button>`;
  }

  /**
   * getCardElement returns The HTML for the card element to be associated to
   * the community where the site plan belongs.
   */
  getCardElement() {
    const hoverCard = document.createElement('div');
    hoverCard.setAttribute('hidden', '');
    hoverCard.classList.add('nhs-c-tooltip--map', 'site-plan_tooltip');
    hoverCard.dataset.id = this.communityId;
    hoverCard.dataset.tooltip = true;

    hoverCard.innerHTML = `
        <p class="nhs-c-tooltip__title-container">${this.priceText}</p>
        <p>${this.title}</p>
        <p class="nhs-c-tooltip__facts">${this.detailText}</p>
        ${this.getInteractiveSitePlanSection()}`;

    return hoverCard;
  }

  /**
   * getCardElementWithPriceReduction: returns the HTML for the card element to be associated to
   * the community site plan.
   */
  getCardElementWithPriceReduction() {
    const hoverCard = document.createElement('div');
    hoverCard.setAttribute('hidden', '');
    hoverCard.classList.add('nhs-c-tooltip--map', 'site-plan_tooltip');
    hoverCard.dataset.id = this.communityId;
    hoverCard.dataset.tooltip = true;

    hoverCard.innerHTML = `
      <p class="nhs-c-tooltip__title-container">${this.priceText}</p>
      <p>${this.title}</p>
      <p class="nhs-c-tooltip__price-reduction" data-qa="price-reduction">
        <span class='nhs-c-tooltip__price-reduction-icon'>${priceReducedSvgHtml}</span>
        ${this.priceReducedText}
        </p>
     <p class="nhs-c-tooltip__facts">${this.detailText}</p>
     ${this.getInteractiveSitePlanSection()}`;

    return hoverCard;
  }

  /**
   * getCard: returns the tooltip html.
   */
  getCard() {
    if (this.priceReducedText) {
      return this.getCardElementWithPriceReduction();
    }

    return this.getCardElement();
  }
}
